import { Error, ExpandMore, MoreHoriz } from '@material-ui/icons';
import { Grid, Accordion, AccordionDetails, AccordionSummary, Collapse, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import Truncate from 'components/Truncate';
import * as React from 'react';
import PermissionHide from 'v2/components/Shared/PermissionHide';

import { Actions } from './Actions';
import { Filter } from './Filter';
import { useWebhookContext } from '../context';
import { History } from '../History';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from '../interfaces';
import useStyles from '../styles';

import type { Webhook } from '../interfaces';
export const List = () => {
  const classes = useStyles();

  const {
    loading,
    webhooks,
    filter,
    expanded,
    selectedWebhook,
    anchorEl,
    isActionsOpen,
    listWebhooks,
    setSelectedWebhook,
    setAnchorEl,
    setIsActionsOpen,
    setExpanded,
  } = useWebhookContext();

  const [url, setUrl] = React.useState<string>();

  const handleClickActions = (e: React.MouseEvent<HTMLElement>, w: Webhook) => {
    e.stopPropagation();
    setSelectedWebhook(w);
    setAnchorEl(e.currentTarget);
    setIsActionsOpen(true);
  };

  const handleCopy = (e: React.SyntheticEvent, w: Webhook) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w.url);
    setUrl(w.url);
  };

  const onCollapse = (e: React.SyntheticEvent, panel: number) => {
    e.stopPropagation();
    if (panel === expanded) return setExpanded(-1);
    setExpanded(panel);
    window.scrollTo(0, 0);
  };

  React.useEffect(() => {
    listWebhooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const urlToLowerCase = filter?.url?.toLowerCase();

  const filtered = webhooks
    ?.filter((w) =>
      !urlToLowerCase ? true : w?.url?.toLowerCase().includes(urlToLowerCase) || w.description?.toLowerCase().includes(urlToLowerCase)
    )
    .filter((f) => (filter?.status === 'all' ? true : f.state === filter?.status))
    .filter((g) => (filter?.type === 'all' ? true : g.type === filter?.type));

  if (loading) {
    return (
      <>
        {[...Array(10)].map((n) => (
          <Skeleton key={n} variant='rounded' style={{ marginTop: 12 }} width={'100%'} height={80} />
        ))}
      </>
    );
  }
  const isCollapseExpanded = expanded !== -1;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Filter />
        </Grid>
        <Grid item xs={12}>
          {filtered?.length === 0 && (
            <Typography className={classes.notFound} color='inherit'>
              Nenhum webhook encontrado
            </Typography>
          )}
          {filtered.map((w, index) => {
            const isExpanded = expanded === index;
            const isCurrentSelected = selectedWebhook?._id === w._id && anchorEl;
            return (
              <Collapse in={isCollapseExpanded ? isExpanded : true} key={w._id}>
                <Accordion
                  sx={{
                    border: isCurrentSelected ? '1px solid #0000000d' : 'none',
                    boxShadow: isExpanded ? '0 3px 6px -4px #0000001f, 0 6px 16px #00000014, 0 9px 28px 8px #0000000d' : '0px 0px 1px 0px #0000001f',
                  }}
                  TransitionProps={{ unmountOnExit: true }}
                  expanded={isExpanded}
                  disableGutters
                  className={classes.accordion}
                >
                  <AccordionSummary
                    expandIcon={
                      <IconButton onClick={(e) => onCollapse(e, index)}>
                        <ExpandMore />
                      </IconButton>
                    }
                    aria-controls={`panel-${w.description}`}
                    id={`panel-${w.description}`}
                  >
                    <div className={classes.accordionSummary}>
                      <Grid container spacing={2} alignItems='center'>
                        <Grid item xs={12} md={12} lg={4}>
                          <Typography>
                            <b>
                              <Truncate count={35} final='...' title={w.description}>
                                {w.description}
                              </Truncate>
                            </b>
                            <Grid container alignItems='baseline'>
                              <Grid item>
                                <Typography className={`${classes.status} ${w.state}`}>{WebhookStatusAlias[w.state]}</Typography>
                              </Grid>
                              <Grid item>
                                {w.state === WebhookStatus.failing && (
                                  <Tooltip title='Ocorreu um erro ao tentar enviar webhook para a url cadastrada, por favor verifique a url e ative novamente o webhook'>
                                    <IconButton size='small' color='error'>
                                      <Error className={classes.errorIcon} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Grid>
                            </Grid>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={12} lg={5}>
                          <Tooltip title={url === w.url ? 'Copiado!' : 'Clique para copiar'}>
                            <Typography className={classes.url} onClick={(e) => handleCopy(e, w)}>
                              <Truncate count={40} final='...' title={w.url}>
                                {w.url}
                              </Truncate>
                            </Typography>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12} md={12} lg={2}>
                          <Typography className={classes.type}>{WebhookTypeAlias[w.type]}</Typography>
                        </Grid>
                        <PermissionHide action={'myeduzz:webhook:editar'}>
                          <Grid item xs={12} md={1}>
                            <Tooltip title='Ações'>
                              <IconButton
                                onClick={(e) => handleClickActions(e, w)}
                                size='small'
                                sx={{ ml: 2 }}
                                aria-controls={isActionsOpen ? 'account-menu' : undefined}
                                aria-haspopup='true'
                                aria-expanded={isActionsOpen ? 'true' : undefined}
                              >
                                <MoreHoriz />
                              </IconButton>
                            </Tooltip>
                          </Grid>
                        </PermissionHide>
                        {isExpanded && (
                          <>
                            <Grid item xs={12} md={12} lg={4}>
                              <Typography className={classes.type}>
                                <Truncate count={25} final='...' title={w.product_name}>
                                  <strong>Produto: </strong>
                                  {w.product_name}
                                </Truncate>
                              </Typography>
                            </Grid>
                            {w.state === WebhookStatus.failing && (
                              <Grid item xs={12} md={12} lg={8}>
                                <Typography className={classes.type}>
                                  <strong>Eventos em espera: </strong>
                                  {w.inQueueCount}
                                </Typography>
                              </Grid>
                            )}
                          </>
                        )}
                      </Grid>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <History id={w._id} type={w.type} productId={w.product_id} />
                  </AccordionDetails>
                </Accordion>
              </Collapse>
            );
          })}
        </Grid>
      </Grid>
      <Actions />
    </>
  );
};
